import { createElement } from "react";

import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { colors } from "@rubi/ui";
import ReactDOMServer from "react-dom/server";
import { ElementFactory, Question, Serializer, SvgRegistry } from "survey-core";
import { localization } from "survey-creator-core";
import { PropertyGridEditorCollection } from "survey-creator-react";
import {
  ReactQuestionFactory,
  SurveyQuestionElementBase,
} from "survey-react-ui";

import { tQuestion } from "@/components/online-reporting/custom-questions/i18nQuestion";

const CURRENCY_INPUT_TYPE = "currency-input";

export class QuestionCurrencyInputModel extends Question {
  getType() {
    return CURRENCY_INPUT_TYPE;
  }

  get hardMin(): number | undefined {
    return this.getPropertyValue("hardMin");
  }
  set hardMin(val: number | undefined) {
    this.setPropertyValue("hardMin", val);
  }

  get hardMax(): number | undefined {
    return this.getPropertyValue("hardMax");
  }
  set hardMax(val: number | undefined) {
    this.setPropertyValue("hardMax", val);
  }

  get min(): number | undefined {
    return this.getPropertyValue("min");
  }
  set min(val: number | undefined) {
    this.setPropertyValue("min", val);
  }

  get max(): number | undefined {
    return this.getPropertyValue("max");
  }
  set max(val: number | undefined) {
    this.setPropertyValue("max", val);
  }

  get minErrorText(): string {
    return this.getPropertyValue("minErrorText");
  }
  set minErrorText(val: string) {
    this.setPropertyValue("minErrorText", val);
  }

  get maxErrorText(): string {
    return this.getPropertyValue("maxErrorText");
  }
  set maxErrorText(val: string) {
    this.setPropertyValue("maxErrorText", val);
  }

  clampValue(numericValue: number): number {
    if (this.hardMin != null && numericValue < this.hardMin)
      return this.hardMin;
    if (this.hardMax != null && numericValue > this.hardMax)
      return this.hardMax;
    return numericValue;
  }

  validate(fireCallback?: boolean, focusFirstError?: boolean): boolean {
    const baseValid = super.validate(fireCallback, focusFirstError);
    if (!this.value) return baseValid;

    const numericValue = parseFloat(
      this.value.toString().replace(/[^\d.]/g, ""),
    );
    if (isNaN(numericValue)) return baseValid;

    if (this.min != null && numericValue < this.min) {
      this.addError(
        this.minErrorText ||
          tQuestion("currencyInput.minValueError", {
            min: this.min.toFixed(2),
          }),
      );
      return false;
    }
    if (this.max != null && numericValue > this.max) {
      this.addError(
        this.maxErrorText ||
          tQuestion("currencyInput.maxValueError", {
            max: this.max.toFixed(2),
          }),
      );
      return false;
    }
    return baseValid;
  }
}

// Guard against duplicate registration (can happen with HMR or multiple imports)
export function registerCurrencyInput() {
  if (!ElementFactory.Instance.getAllTypes().includes(CURRENCY_INPUT_TYPE)) {
    ElementFactory.Instance.registerElement(CURRENCY_INPUT_TYPE, (name) => {
      return new QuestionCurrencyInputModel(name);
    });
  }
}

// Specify display names for the question type
// i18n: builder label deferred
const locale = localization.getLocale("");
locale.qt[CURRENCY_INPUT_TYPE] = "Currency Input";

// Property-grid help text. SurveyJS surfaces property descriptions through the
// `pehelp` localization map keyed by property name, not via the property info object.
locale.pehelp.hardMin =
  "Prevents entry below this value. The input will be clamped to this minimum on blur.";
locale.pehelp.hardMax =
  "Prevents entry above this value. The input will be clamped to this maximum on blur.";

const SURVEY_ICON_COLOR = colors.text.disabled;
const svg = ReactDOMServer.renderToString(
  <AttachMoneyIcon style={{ fill: SURVEY_ICON_COLOR }} />,
);
SvgRegistry.registerIconFromSvg(CURRENCY_INPUT_TYPE, svg);

// Guard against duplicate registration (can happen with HMR or multiple imports)
if (!Serializer.findClass(CURRENCY_INPUT_TYPE)) {
  Serializer.addClass(
    CURRENCY_INPUT_TYPE,
    [
      {
        name: "hardMin:number",
        category: "validation",
        displayName: "Minimum value (hard block)",
      },
      {
        name: "hardMax:number",
        category: "validation",
        displayName: "Maximum value (hard block)",
      },
      {
        name: "min:number",
        category: "validation",
        displayName: "Minimum value",
      },
      {
        name: "max:number",
        category: "validation",
        displayName: "Maximum value",
      },
      {
        name: "minErrorText",
        category: "validation",
        displayName: "Minimum value error message",
      },
      {
        name: "maxErrorText",
        category: "validation",
        displayName: "Maximum value error message",
      },
    ],
    function () {
      return new QuestionCurrencyInputModel("");
    },
    "question",
  );
}

export class SurveyQuestionCurrencyInput extends SurveyQuestionElementBase {
  constructor(props: any) {
    super(props);
    this.state = {
      displayValue: this.formatCurrency(this.question.value || ""),
    };
  }

  componentDidUpdate(prevProps: any) {
    if (this.question.value !== prevProps.question?.value) {
      this.setState({
        displayValue: this.formatCurrency(this.question.value || ""),
      });
    }
  }
  get question() {
    return this.questionBase;
  }

  get value() {
    return this.question.value;
  }

  formatCurrency(value: string | number): string {
    if (!value) return "";

    const numericValue = value.toString().replace(/[^\d.]/g, "");
    const parsed = parseFloat(numericValue);

    if (isNaN(parsed)) return "";

    return parsed.toFixed(2);
  }

  handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    const numericValue = inputValue.replace(/[^\d.]/g, "");

    this.question.value = numericValue;
    this.setState({ displayValue: numericValue });
  };

  handleBlur = () => {
    const raw = this.question.value;
    if (!raw) return;

    const parsed = parseFloat(raw.toString().replace(/[^\d.]/g, ""));
    if (isNaN(parsed)) return;

    const clamped = (this.question as QuestionCurrencyInputModel).clampValue(
      parsed,
    );
    const formatted = clamped.toFixed(2);
    this.question.value = formatted;
    this.setState({ displayValue: formatted });
  };

  get style() {
    return this.question.getPropertyValue("readOnly") ||
      this.question.isDesignMode
      ? { pointerEvents: "none" }
      : {};
  }

  renderElement() {
    return (
      <input
        className="sd-input sd-text"
        type="text"
        inputMode="decimal"
        value={this.state.displayValue}
        onChange={this.handleChange}
        onBlur={this.handleBlur}
        disabled={this.props.isDisplayMode || this.question.isReadOnly}
        placeholder="0.00"
      />
    );
  }
}

ReactQuestionFactory.Instance.registerQuestion(CURRENCY_INPUT_TYPE, (props) => {
  return createElement(SurveyQuestionCurrencyInput, props);
});

// Register property grid editor
PropertyGridEditorCollection.register({
  fit: function (prop: { type: string }) {
    return prop.type === "currency";
  },
  getJSON: function () {
    return {
      type: CURRENCY_INPUT_TYPE,
    };
  },
});
